import { useContext, useEffect, useRef, useState } from 'react';
import {
  BoxSection,
  ButtonDefault,
  ButtonPrimary,
  Spinner,
  TextL,
} from 'app/components';
import { ButtonsArea, SimulationStepContainer } from './styles';
import { ApplicationRequestContext } from '../ApplicationRequest';
import {
  CreditSimulator,
  CreditSimulatorRefs,
} from 'modules/products/pages/ApplicationRequest/SimulationV2Step/CreditSimulatorV2';
import { ArrowBack } from '@mui/icons-material';
import { useTheme } from '@mui/material';
import { ConsigneeOrganType } from '../ConsignedStep/interfaces';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { IElegibility, IElegibilityConfigs } from './CreditSimulatorV2/types';
import { formatCurrency } from 'app/utils/normalizer';

const SimulationV2Step = () => {
  /* Hooks */
  const [api, context] = notification.useNotification();
  const theme = useTheme();
  const formRef = useRef<CreditSimulatorRefs>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    product,
    simulationV2,
    simulationV2Request,
    borrowerQualification,
    billetValidationResponse,
    qrcodeEmvValidationResponse,
    updateSimulationV2,
    onBack,
    onForward,
    consignedProduct,
    tacValidationError,
    borrowerData,
    getElegibilityRules,
    elegibilityError,
  } = useContext(ApplicationRequestContext);

  /* States */
  const [tabIndexSelected, setTabIndexSelected] = useState('');
  const [isValidElegibility, setIsValidElegibility] = useState(true);

  /* Variables */
  const isFgts = product?.organ_type === ConsigneeOrganType.FGTS;
  const isSiape =
    product?.organ_type === ConsigneeOrganType.SIAPE &&
    !!consignedProduct?.available_balance;
  const consignedAmount = consignedProduct?.available_balance || 0;
  const consignedParcels = 96;
  const consignedTotalAmount = consignedAmount * consignedParcels;

  // Validação SIAPE por tipo (parcela ou total)
  // e se o valor da parcela está menor que o valor liberado
  const isAmountAllowed =
    tabIndexSelected === '0'
      ? (consignedProduct?.available_balance || 0) >
        (simulationV2?.payment_amount || 0)
      : consignedTotalAmount >= (simulationV2?.requested_amount || 0);

  const isButtonDisabled =
    (isSiape && !isAmountAllowed) ||
    (!formRef.current?.isValid && !!simulationV2?.requested_amount) ||
    !!tacValidationError;

  /* Handlers */
  const handleSimulator = () => {
    formRef.current?.simulate();
  };

  const handleBack = () => {
    formRef.current?.reset();
    updateSimulationV2(null, null, '0'); // Reset simulation state
    onBack();
  };

  const getElegibilityRulesHandler = async (id: string) => {
    const data: IElegibility | null = await getElegibilityRules(id);

    if (data?.configs) {
      validateElegibility(data.configs);
    }
  };

  const validateElegibility = (configs: IElegibilityConfigs) => {
    if (configs) {
      const age = dayjs().diff(borrowerData?.birth_date, 'year');
      const education = borrowerData?.education_level;
      const installments = simulationV2?.num_periods;
      const operationValue = simulationV2?.total_amount_owed;
      const interestRate = simulationV2?.interest_rate;

      // Info: Lembrar de add tambem no useCreditSimulatorV2
      if (
        configs?.age &&
        (age < configs?.age?.min || age > configs?.age?.max)
      ) {
        setIsValidElegibility(false);
        api.error({
          message: 'Idade inválida',
          description: `A idade deve estar entre ${configs?.age?.min} e ${configs?.age?.max} anos.`,
          duration: 10000,
          pauseOnHover: true,
        });
      }

      if (
        !!education &&
        !configs?.education?.allowedDegrees.includes(education)
      ) {
        setIsValidElegibility(false);
        api.error({
          message: 'Grau de instrução inválido',
          duration: 10000,
          description:
            'O grau de escolaridade informado não permite a contratação deste produto',
        });
      }

      if (
        installments &&
        configs?.installments &&
        (installments < configs?.installments.min ||
          installments > configs?.installments.max)
      ) {
        setIsValidElegibility(false);
        api.error({
          message: 'Parcelas inválidas',
          duration: 10000,
          description: `O número de parcelas deve estar entre ${configs?.installments.min} e ${configs?.installments.max}.`,
        });
      }

      if (
        interestRate &&
        configs?.interestRate &&
        (interestRate < configs?.interestRate.min ||
          interestRate > configs?.interestRate.max)
      ) {
        setIsValidElegibility(false);
        api.error({
          message: 'Taxa de juros inválida',
          duration: 10000,
          description: `A taxa de juros deve estar entre ${configs?.interestRate.min} e ${configs?.interestRate.max}.`,
        });
      }

      if (
        operationValue &&
        configs?.operationValue &&
        (operationValue < configs?.operationValue.min ||
          operationValue > configs?.operationValue.max)
      ) {
        setIsValidElegibility(false);
        api.error({
          message: 'Valor de operação inválido',
          duration: 10000,
          description: `O valor da operação deve estar entre ${formatCurrency(
            configs?.operationValue.min,
          )} e ${formatCurrency(configs?.operationValue.max)}.`,
        });
      }
    }
  };

  useEffect(() => {
    if (product?.id) {
      getElegibilityRulesHandler(product?.id);
    }
  }, [product?.id]);

  useEffect(() => {
    if (elegibilityError?.message) {
      api.error({
        message: elegibilityError.message,
        description: elegibilityError?.description || '',
        duration: 10000,
        pauseOnHover: true,
      });
    }
  }, [elegibilityError]);

  // FORA DE ESCOPO
  //const downloadSimulationHandler = useCallback(async () => {
  //  const file = await downloadSimulation(consignedId);
  //
  //  if (file) {
  //    saveAs(
  //      new Blob([file], {
  //        type: 'application/pdf',
  //      }),
  //      `simulacao_${consignedId}.pdf`,
  //    );
  //  }
  //}, [consignedId]);

  return (
    <>
      {context}
      <BoxSection>
        <SimulationStepContainer ref={containerRef}>
          <CreditSimulator
            ref={formRef}
            product={product}
            simulationResponse={simulationV2}
            simulationRequest={simulationV2Request}
            billetValidationResponse={billetValidationResponse}
            qualification={borrowerQualification}
            qrcodeEmvValidationResponse={qrcodeEmvValidationResponse}
            onSimulate={(simulation, simulationRequest, tabIndex) => {
              setTabIndexSelected(tabIndex);
              updateSimulationV2(simulation, simulationRequest, tabIndex);
              setTimeout(() =>
                containerRef.current?.scrollTo({
                  top: 9999,
                  behavior: 'smooth',
                }),
              );
            }}
          />
        </SimulationStepContainer>
        <ButtonsArea>
          <div>
            {!isFgts && (
              <ButtonDefault
                style={{
                  maxWidth: '200px',
                }}
                onClick={() => formRef.current?.reset()}
              >
                <TextL style={{ color: theme.palette.brand.primary.base }}>
                  Limpar condições
                </TextL>
              </ButtonDefault>
            )}
          </div>
          {formRef.current?.isLoading ? (
            <Spinner />
          ) : (
            <div className="btn-preview">
              <ButtonDefault onClick={handleBack}>
                <ArrowBack />
              </ButtonDefault>
              {simulationV2 && simulationV2.requested_amount && (
                <>
                  <ButtonDefault onClick={() => formRef.current?.copyPreview()}>
                    <TextL
                      style={{
                        color: theme.palette.brand.primary.base,
                        minWidth: '200px',
                      }}
                    >
                      Copiar resultados
                    </TextL>
                  </ButtonDefault>
                  <ButtonDefault
                    style={{ color: theme.palette.brand.primary.base }}
                    onClick={handleSimulator}
                  >
                    <TextL style={{ color: theme.palette.brand.primary.base }}>
                      Recalcular
                    </TextL>
                  </ButtonDefault>
                </>
              )}
              <ButtonPrimary
                disabled={isButtonDisabled || !isValidElegibility}
                onClick={
                  !simulationV2?.requested_amount ? handleSimulator : onForward
                }
              >
                <TextL style={{ color: theme.palette.common.white }}>
                  {!simulationV2?.requested_amount ? 'Calcular' : 'Avançar'}
                </TextL>
              </ButtonPrimary>
            </div>
          )}
        </ButtonsArea>
      </BoxSection>
    </>
  );
};

export default SimulationV2Step;
